<template>
  <section class="cover-picker" :aria-label="$t('publishUi.coverTitle')">
    <header>
      <div>
        <strong>{{ $t("publishUi.coverTitle") }}</strong>
        <span>{{ $t("publishUi.coverSubtitle") }}</span>
      </div>
      <time>{{ formatTime(selectedTime) }} / {{ formatTime(duration) }}</time>
    </header>

    <div class="cover-stage" :class="{ loading: selecting }">
      <img v-if="previewUrl" :src="previewUrl" :alt="$t('publishUi.coverPreviewAlt')">
      <div v-else class="cover-placeholder">
        <i class="el-icon-loading" />
        <span>{{ $t("publishUi.coverGenerating") }}</span>
      </div>
      <span v-if="selecting && previewUrl" class="cover-loading"><i class="el-icon-loading" /></span>
    </div>

    <div class="cover-timeline" :class="{ disabled: !ready }">
      <div class="cover-frames" aria-hidden="true">
        <span v-for="(frame, index) in frames" :key="`${index}-${frame.slice(-16)}`">
          <img :src="frame" alt="">
        </span>
        <span v-for="index in emptyFrameCount" :key="`empty-${index}`" class="empty-frame"><i class="el-icon-picture-outline" /></span>
      </div>
      <span class="cover-cursor" :style="{ insetInlineStart: `${progress}%` }"><i /></span>
      <input
        :value="selectedTime"
        data-analytics="publish.cover_frame_select"
        type="range"
        min="0"
        :max="duration || 0"
        step="0.05"
        :disabled="!ready || selecting"
        :aria-label="$t('publishUi.coverSlider')"
        @input="handleScrub"
        @change="commitScrub"
      >
    </div>

    <p><i class="el-icon-position" />{{ $t("publishUi.coverHint") }}</p>

    <video ref="extractor" class="cover-source" :src="src" muted playsinline preload="auto" @loadedmetadata="handleMetadata" @error="handleVideoError" />
    <video ref="thumbnailExtractor" class="cover-source" :src="src" muted playsinline preload="auto" />
    <canvas ref="canvas" class="cover-source" />
  </section>
</template>

<script>
const FRAME_COUNT = 8

export default {
  name: "VideoCoverPicker",
  props: {
    src: { type: String, required: true }
  },
  data() {
    return {
      duration: 0,
      selectedTime: 0,
      previewUrl: "",
      frames: [],
      ready: false,
      selecting: false,
      scrubTimer: null,
      sourceVersion: 0,
      captureVersion: 0
    }
  },
  computed: {
    progress() {
      return this.duration > 0 ? Math.min(100, Math.max(0, this.selectedTime / this.duration * 100)) : 0
    },
    emptyFrameCount() {
      return Math.max(0, FRAME_COUNT - this.frames.length)
    }
  },
  watch: {
    src() {
      this.resetPicker()
    }
  },
  beforeDestroy() {
    window.clearTimeout(this.scrubTimer)
    this.sourceVersion += 1
    this.captureVersion += 1
  },
  methods: {
    resetPicker() {
      window.clearTimeout(this.scrubTimer)
      this.sourceVersion += 1
      this.captureVersion += 1
      this.duration = 0
      this.selectedTime = 0
      this.previewUrl = ""
      this.frames = []
      this.ready = false
      this.selecting = false
      this.$emit("loading", true)
      this.$nextTick(() => {
        if (this.$refs.extractor) this.$refs.extractor.load()
        if (this.$refs.thumbnailExtractor) this.$refs.thumbnailExtractor.load()
      })
    },
    async handleMetadata() {
      const video = this.$refs.extractor
      const duration = Number(video && video.duration)
      if (!Number.isFinite(duration) || duration <= 0 || !video.videoWidth || !video.videoHeight) {
        this.handleVideoError()
        return
      }

      const version = ++this.sourceVersion
      const captureVersion = ++this.captureVersion
      this.duration = duration
      this.selectedTime = Math.min(Math.max(duration * 0.1, 0.1), Math.max(0, duration - 0.05))
      this.ready = true
      this.selecting = true
      this.$emit("loading", true)

      try {
        await this.captureSelected(version, captureVersion)
        if (version !== this.sourceVersion || captureVersion !== this.captureVersion) return
        this.selecting = false
        this.$emit("loading", false)
        this.generateFrameStrip(version)
      } catch (error) {
        if (version === this.sourceVersion) this.handleVideoError(error)
      }
    },
    handleScrub(event) {
      this.selectedTime = Number(event.target.value) || 0
      window.clearTimeout(this.scrubTimer)
      this.scrubTimer = window.setTimeout(() => this.selectCurrentFrame(), 140)
    },
    commitScrub(event) {
      this.selectedTime = Number(event.target.value) || 0
      window.clearTimeout(this.scrubTimer)
      this.selectCurrentFrame()
    },
    async selectCurrentFrame() {
      if (!this.ready || this.selecting) return
      const version = this.sourceVersion
      const captureVersion = ++this.captureVersion
      this.selecting = true
      this.$emit("loading", true)
      try {
        await this.captureSelected(version, captureVersion)
      } catch (error) {
        if (version === this.sourceVersion) this.handleVideoError(error)
        return
      }
      if (version !== this.sourceVersion || captureVersion !== this.captureVersion) return
      this.selecting = false
      this.$emit("loading", false)
    },
    async captureSelected(version, captureVersion) {
      const video = this.$refs.extractor
      await this.seek(video, this.selectedTime)
      if (version !== this.sourceVersion || captureVersion !== this.captureVersion) return

      const canvas = this.$refs.canvas
      const scale = Math.min(1, 720 / video.videoWidth)
      canvas.width = Math.max(1, Math.round(video.videoWidth * scale))
      canvas.height = Math.max(1, Math.round(video.videoHeight * scale))
      const context = canvas.getContext("2d")
      context.drawImage(video, 0, 0, canvas.width, canvas.height)
      const blob = await this.canvasBlob(canvas)
      if (version !== this.sourceVersion || captureVersion !== this.captureVersion) return

      const file = new File([blob], `starlive-cover-${Date.now()}.jpg`, { type: "image/jpeg", lastModified: Date.now() })
      this.previewUrl = canvas.toDataURL("image/jpeg", 0.86)
      this.$emit("change", { file, previewUrl: this.previewUrl, time: this.selectedTime, duration: this.duration })
    },
    async generateFrameStrip(version) {
      const video = this.$refs.thumbnailExtractor
      try {
        await this.waitForMetadata(video)
        const frames = []
        for (let index = 0; index < FRAME_COUNT; index += 1) {
          if (version !== this.sourceVersion) return
          const time = this.duration * ((index + 0.5) / FRAME_COUNT)
          await this.seek(video, time)
          frames.push(this.thumbnailDataUrl(video))
        }
        if (version === this.sourceVersion) this.frames = frames
      } catch (error) {
        // 缩略帧失败不影响已经生成的主封面，时间轴仍可继续拖动。
      }
    },
    thumbnailDataUrl(video) {
      const canvas = document.createElement("canvas")
      canvas.width = 80
      canvas.height = 112
      const sourceRatio = video.videoWidth / video.videoHeight
      const targetRatio = canvas.width / canvas.height
      let sourceWidth = video.videoWidth
      let sourceHeight = video.videoHeight
      let sourceX = 0
      let sourceY = 0
      if (sourceRatio > targetRatio) {
        sourceWidth = video.videoHeight * targetRatio
        sourceX = (video.videoWidth - sourceWidth) / 2
      } else {
        sourceHeight = video.videoWidth / targetRatio
        sourceY = (video.videoHeight - sourceHeight) / 2
      }
      canvas.getContext("2d").drawImage(video, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, canvas.width, canvas.height)
      return canvas.toDataURL("image/jpeg", 0.62)
    },
    seek(video, time) {
      return new Promise((resolve, reject) => {
        if (!video) { reject(new Error("video unavailable")); return }
        const target = Math.min(Math.max(0, time), Math.max(0, Number(video.duration) - 0.01))
        if (video.readyState >= 2 && Math.abs(video.currentTime - target) < 0.025) { resolve(); return }
        let timer = null
        const cleanup = () => {
          window.clearTimeout(timer)
          video.removeEventListener("seeked", onSeeked)
          video.removeEventListener("error", onError)
        }
        const onSeeked = () => { cleanup(); resolve() }
        const onError = () => { cleanup(); reject(new Error("video seek failed")) }
        video.addEventListener("seeked", onSeeked, { once: true })
        video.addEventListener("error", onError, { once: true })
        timer = window.setTimeout(() => { cleanup(); reject(new Error("video seek timeout")) }, 5000)
        try { video.currentTime = target } catch (error) { cleanup(); reject(error) }
      })
    },
    waitForMetadata(video) {
      if (video && video.readyState >= 1 && Number.isFinite(video.duration)) return Promise.resolve()
      return new Promise((resolve, reject) => {
        if (!video) { reject(new Error("video unavailable")); return }
        let timer = null
        const cleanup = () => {
          window.clearTimeout(timer)
          video.removeEventListener("loadedmetadata", onReady)
          video.removeEventListener("error", onError)
        }
        const onReady = () => { cleanup(); resolve() }
        const onError = () => { cleanup(); reject(new Error("metadata unavailable")) }
        video.addEventListener("loadedmetadata", onReady, { once: true })
        video.addEventListener("error", onError, { once: true })
        timer = window.setTimeout(() => { cleanup(); reject(new Error("metadata timeout")) }, 5000)
        video.load()
      })
    },
    canvasBlob(canvas) {
      return new Promise((resolve, reject) => {
        canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error("cover encoding failed")), "image/jpeg", 0.86)
      })
    },
    handleVideoError(error) {
      this.ready = false
      this.selecting = false
      this.$emit("loading", false)
      this.$emit("error", error || new Error("video decode failed"))
    },
    formatTime(value) {
      const seconds = Math.max(0, Math.floor(Number(value) || 0))
      const minutes = Math.floor(seconds / 60)
      return `${String(minutes).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`
    }
  }
}
</script>

<style scoped>
.cover-picker{margin:14px 0;padding:14px;border:1px solid #303137;border-radius:18px;background:linear-gradient(145deg,#18191e,#121317);box-shadow:0 12px 28px rgba(0,0,0,.18)}.cover-picker header{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:12px}.cover-picker header div{min-width:0}.cover-picker header strong,.cover-picker header span{display:block}.cover-picker header strong{color:#f7f4f1;font-size:14px}.cover-picker header span{margin-top:4px;color:#85878f;font-size:11px;line-height:1.45}.cover-picker time{flex:none;padding:5px 8px;border-radius:999px;background:#25262c;color:#c6c7cc;font-size:10px;font-variant-numeric:tabular-nums}.cover-stage{position:relative;width:112px;aspect-ratio:9/14;margin:0 auto 14px;overflow:hidden;border:1px solid rgba(255,91,112,.32);border-radius:14px;background:#0a0b0e;box-shadow:0 12px 28px rgba(0,0,0,.3)}.cover-stage img{width:100%;height:100%;display:block;object-fit:cover}.cover-stage.loading img{opacity:.72}.cover-placeholder{height:100%;display:grid;place-content:center;justify-items:center;gap:8px;color:#787a82;font-size:9px;text-align:center}.cover-placeholder i{color:#ff6075;font-size:20px}.cover-loading{position:absolute;inset:0;display:grid;place-items:center;background:rgba(8,9,12,.35);color:#fff;font-size:22px}.cover-timeline{position:relative;height:70px;overflow:hidden;border:2px solid #ff5a70;border-radius:12px;background:#202126}.cover-timeline.disabled{opacity:.58}.cover-frames{position:absolute;inset:0;display:grid;grid-template-columns:repeat(8,minmax(0,1fr))}.cover-frames span{min-width:0;overflow:hidden;background:#202126}.cover-frames img{width:100%;height:100%;display:block;object-fit:cover}.empty-frame{display:grid;place-items:center;border-inline-end:1px solid #292a30;color:#454750}.cover-cursor{position:absolute;z-index:2;top:-2px;bottom:-2px;width:3px;transform:translateX(-50%);border-radius:4px;background:#fff;box-shadow:0 0 0 1px rgba(0,0,0,.38),0 0 12px rgba(255,255,255,.62);pointer-events:none}.cover-cursor i{position:absolute;top:50%;left:50%;width:13px;height:20px;transform:translate(-50%,-50%);border:2px solid #fff;border-radius:5px;background:#ff526a}.cover-timeline input{position:absolute;z-index:3;inset:0;width:100%;height:100%;margin:0;opacity:0;cursor:ew-resize}.cover-timeline input:disabled{cursor:not-allowed}.cover-picker>p{display:flex;align-items:flex-start;gap:6px;margin:10px 2px 0;color:#797b83;font-size:10px;line-height:1.5}.cover-picker>p i{margin-top:2px;color:#ff6075}.cover-source{position:fixed!important;width:1px!important;height:1px!important;inset:auto!important;opacity:0!important;pointer-events:none!important}[dir="rtl"] .cover-cursor{transform:translateX(50%)}
</style>
